import i18next from 'i18next'
import React from 'react'
import { browserRouter, Widget, any, async, fromJSX, stateful, Updater, Redirect } from 'widgets-for-react'
import { SPARedirect } from '../..'
import { ComponentDidUpdate } from '../utils/lifecycleMethods'
import { cmsPageWidget } from './cmsPage/cmsPageWidget'
import { CookiebarState, cookiebarStateUpdaters } from './cookiebar/cookiebarState'
import { cookiebarWidget } from './cookiebar/cookiebarWidget'
import { crisisBannerWidget } from './crisisbanner/crisisbannerWidget'
import { departmentsAndCentraOverviewWidget } from './departmentsAndCentraOverview/departmentsAndCentraOverviewWidget'
import { verpleegafdelingenOverviewWidget } from './verpleegafdelingenOverview/verpleegafdelingenOverviewWidget'
import { eventOverviewWidget } from './eventOverview/eventOverviewWidget'
import { foldersWidget } from './folders/foldersWidget'
import { footerWidget } from './footer/footerWidget'
import { initialHeaderState } from './header/headerState'
import { headerWidget } from './header/headerWidget'
import { newsAndEventsOverviewWidget } from './newsAndEventsOverview/newsAndEventsOverviewWidget'
import { newsOverviewWidget } from './newsOverview/newsOverviewWidget'
import { notFoundWidget } from './notFound/notFoundWidget'
import { researchOverviewWidget } from './researchOverview/researchOverviewWidget'
import { RootLayout } from './rootLayout'
import { initialRootState, RootState, rootStateUpdaters } from './rootState'
import { pageParamToPage } from './routes/routesState'
import { routesWidget } from './routes/routesWidget'
import { searchPageWidget } from './searchPage/searchPageWidget'
import { specialistOverviewWidget } from './specialistOverview/specialistOverviewWidget'
import { viewListenerWidget } from './viewListener/viewListenerWidget'
import { jotformEventsWidget } from './cmsPage/blocks/block/jotFormsBlock/jotformEventsWidget'
import { setConsentCookie } from '../utils/datalayer.mapping'
import { isEmbedded } from '../utils/embed'

export const rootWidget = (redirects: SPARedirect[]) =>
  browserRouter()(
    stateful()<RootState>((state) =>
      fromJSX<Updater<RootState>>((setState) => {
        const setCookiebarState = (updater: Updater<CookiebarState>) =>
          setState(rootStateUpdaters.updateCookiebarState(updater))
        const acceptVideoCookie = () => {
          setConsentCookie('video-cookies', 'yes')
          setCookiebarState(
            cookiebarStateUpdaters.selectedCookiesUpdater(
              state.cookiebarState.cookies.set('video-cookies', 'yes'),
              true
            )
          )
        }
        const showCookiebar = () =>
          setCookiebarState(cookiebarStateUpdaters.visibleUpdater(!state.cookiebarState.visible))
        const embed = isEmbedded()
        return any<Updater<RootState>>([
          routesWidget(redirects),
          languageSwitch(state),
          jotformEventsWidget(),
          viewListenerWidget.map(rootStateUpdaters.updateViewListenerState),
          // Chrome is suppressed in embed mode: the embedding site provides its own
          // navigation, and consent is handled there too.
          ...(embed
            ? []
            : [
                cookiebarWidget(state.cookiebarState).map(rootStateUpdaters.updateCookiebarState),
                headerWidget(state.view)(state.headerState).map(rootStateUpdaters.updateHeaderState),
              ]),
          async<string[]>(state.navigationItems).map(rootStateUpdaters.navigationItemsUpdater),
          any<Updater<RootState>>([
            ...(embed
              ? []
              : [
                  crisisBannerWidget(acceptVideoCookie)(state.crisisBannerState).map(
                    rootStateUpdaters.updateCrisisBannerState
                  ),
                ]),
            pageSwitch(acceptVideoCookie)(state),
          ]).wrapHTML((element) => (
            <RootLayout
              kind={
                state.page.kind === 'preview'
                  ? state.page.pageState.pageState.kind === 'loaded'
                    ? state.page.pageState.pageState.value.kind
                    : ''
                  : state.page.kind
              }
              inCentrum={
                ((state.page.kind === 'departmentOrCentrum' || state.page.kind === 'preview') &&
                  state.page.pageState.pageState.kind === 'loaded' &&
                  state.page.pageState.pageState.value.kind === 'centrum') ||
                (state.page.kind === 'subDepartmentOrDepartmentOrSubCentrumOrSpaarne' &&
                  state.page.pageState.pageState.kind === 'loaded' &&
                  (state.page.pageState.pageState.value.kind === 'department' ||
                    state.page.pageState.pageState.value.kind === 'spaarne' ||
                    (state.page.pageState.pageState.value.kind === 'subdepartment' &&
                      state.page.pageState.pageState.value.subDepartment !== undefined &&
                      state.page.pageState.pageState.value.subDepartment.centrum !== undefined) ||
                    (state.page.pageState.pageState.value.kind === 'subcentra' &&
                      state.page.pageState.pageState.value.subCentra !== undefined &&
                      state.page.pageState.pageState.value.subCentra.centrum !== undefined))) ||
                (state.page.kind === 'subDepartmentOrSpaarne' && state.page.pageState.pageState.kind === 'loaded')
              }
              embed={embed}
            >
              {element}
            </RootLayout>
          )),
          ...(embed
            ? []
            : [footerWidget(showCookiebar)(state.footerState).map(rootStateUpdaters.updateFooterState)]),
        ]).run((updater) => setState(updater))
      })
    )(initialRootState())
  )

const languageSwitch = (state: RootState): Widget<Updater<RootState>> =>
  fromJSX((setState) => (
    <ComponentDidUpdate
      onUpdate={() => {
        const changeLanguage =
          state.page.kind === 'englishHome' ||
          state.page.kind === 'englishSpaarnePrimary' ||
          state.page.kind === 'englishSpaarneSecondary' ||
          state.page.kind === 'englishSpaarneTernary'
            ? i18next.language === 'en'
              ? false
              : 'en'
            : i18next.language === 'nl'
            ? false
            : 'nl'

        if (changeLanguage !== false) {
          i18next.changeLanguage(changeLanguage)
          //reload header when language is changed
          setState(rootStateUpdaters.updateHeaderState(() => initialHeaderState))
        }
      }}
    />
  ))

const pageSwitch =
  (acceptVideoCookiebarCookie: () => void) =>
  (currentState: RootState): Widget<Updater<RootState>> => {
    switch (currentState.page.kind) {
      case 'redirect':
        const to = currentState.page.to
        return fromJSX(() => <Redirect to={to} />)
      case 'notFound':
        return notFoundWidget(currentState.page.pageState).map(rootStateUpdaters.updateNotFoundPageState)
      case 'home':
      case 'englishHome':
      case 'spaarnePrimary':
      case 'englishSpaarnePrimary':
      case 'spaarneSecondary':
      case 'englishSpaarneSecondary':
      case 'spaarneTernary':
      case 'englishSpaarneTernary':
      case 'event':
      case 'dossierPage':
      case 'dossierChildPage':
      case 'news':
      case 'friend':
      case 'friendHome':
      case 'departmentOrCentrum':
      case 'subDepartmentOrSpaarne':
      case 'subDepartmentOrDepartmentOrSubCentrumOrSpaarne':
      case 'locationOrSpaarne':
      case 'specialist':
      case 'waitingTimes':
      case 'research':
      case 'folderPage':
      case 'preview':
        const navigationItems = currentState.navigationItems.kind == 'loaded' ? currentState.navigationItems.value : []
        return cmsPageWidget(
          acceptVideoCookiebarCookie,
          navigationItems
        )(currentState.page.pageState).map(rootStateUpdaters.updateCmsPageState)

      case 'newsOverview':
        return newsOverviewWidget(
          pageParamToPage(currentState.page.params.page),
          currentState.page.params.department,
          currentState.page.params.subDepartment,
          currentState.page.params.specialist,
          currentState.page.params.centrum,
          currentState.page.params.tags?.split(',')
        )(currentState.page.pageState).map(rootStateUpdaters.updateNewsOverviewState)

      case 'eventOverview':
        return eventOverviewWidget(
          pageParamToPage(currentState.page.params.page),
          currentState.page.params.department,
          currentState.page.params.subDepartment,
          currentState.page.params.specialist,
          currentState.page.params.centrum,
          currentState.page.params.tags?.split(',')
        )(currentState.page.pageState).map(rootStateUpdaters.updateEventOverviewState)

      case 'newsAndEventsOverview':
        return newsAndEventsOverviewWidget(
          pageParamToPage(currentState.page.params.page),
          currentState.page.params.department,
          currentState.page.params.subDepartment,
          currentState.page.params.specialist,
          currentState.page.params.centrum,
          currentState.page.params.tags?.split(',')
        )(currentState.page.pageState).map(rootStateUpdaters.updateNewsAndEventsOverviewState)

      case 'departmentsAndCentraOverview':
        return departmentsAndCentraOverviewWidget(currentState.page.pageState).map(
          rootStateUpdaters.updateDepartmentOverviewState
        )

      case 'verpleegafdelingenOverview':
        return verpleegafdelingenOverviewWidget(currentState.page.pageState).map(
          rootStateUpdaters.updateVerpleegafdelingenOverviewState
        )

      case 'specialistOverview':
        return specialistOverviewWidget(currentState.page.pageState).map(
          rootStateUpdaters.updateSpecialistOverviewState
        )

      case 'researchOverview':
        return researchOverviewWidget(currentState.page.pageState).map(rootStateUpdaters.updateResearchOverviewState)

      case 'search':
        return searchPageWidget(
          currentState.page.pageState,
          currentState.page.params.query || '',
          pageParamToPage(currentState.page.params.page),
          'nl'
        ).map(rootStateUpdaters.updateSearchPage)
      case 'englishSearch':
        return searchPageWidget(
          currentState.page.pageState,
          currentState.page.params.query || '',
          pageParamToPage(currentState.page.params.page),
          'en'
        ).map(rootStateUpdaters.updateSearchPage)

      case 'folders':
        return foldersWidget(currentState.page.pageState).map(rootStateUpdaters.updateFoldersPage)
      default:
        return notFoundWidget('').map(rootStateUpdaters.updateNotFoundPageState)
    }
  }
